import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EventIcon from '@mui/icons-material/Event';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CloseIcon from '@mui/icons-material/Close';
import { useNotification } from '../context/NotificationContext';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    background: 'linear-gradient(90deg,var(--gradient-start),var(--gradient-end))',
    color: 'white',
    fontWeight: 700,
    boxShadow: '0 6px 18px rgba(123,97,255,0.12)'
  },
}));

const NotificationMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    minWidth: '320px',
    boxShadow: 'var(--elevation-3)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,255,255,0.92))',
    backdropFilter: 'blur(8px)'
  },
}));

const NotificationMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    background: 'rgba(123,97,255,0.04)',
  },
}));

const NotificationBell = () => {
  const { notifications, markAsRead, clearNotification } = useNotification();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    // Add navigation logic here if needed
  };

  const getIcon = (type) => {
    switch (type) {
      case 'anniversary':
        return <FavoriteIcon sx={{ color: 'var(--gradient-start)' }} />;
      case 'event':
        return <EventIcon sx={{ color: 'var(--gradient-end)' }} />;
      default:
        return <EventIcon sx={{ color: 'var(--gradient-end)' }} />;
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <>
      <IconButton onClick={handleClick} sx={{ color: 'var(--gradient-end)', '&:hover': { background: 'rgba(123,97,255,0.08)' } }}>
        <StyledBadge badgeContent={unreadCount} max={99}>
          <NotificationsIcon />
        </StyledBadge>
      </IconButton>

      <NotificationMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
        </Box>

        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>No notifications yet</Typography>
          </Box>
        ) : (
          notifications.map((notification) => (
            <NotificationMenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                opacity: notification.read ? 0.7 : 1,
                background: notification.read ? 'transparent' : 'rgba(255, 105, 180, 0.05)',
              }}
            >
              <ListItemIcon>
                {getIcon(notification.type)}
              </ListItemIcon>
              <ListItemText
                primary={notification.title}
                secondary={notification.message}
                sx={{
                  '& .MuiTypography-root': {
                    fontWeight: notification.read ? 'normal' : 'bold',
                  },
                }}
              />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  clearNotification(notification.id);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: '#FF69B4',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </NotificationMenuItem>
          ))
        )}
      </NotificationMenu>
    </>
  );
};

export default NotificationBell; 